/**
 * Method-selection phase of the MFA enrollment modal.
 *
 * Presentational only — the parent modal owns the API calls.
 */

import { useState } from 'react';
import { faCommentSms } from '@fortawesome/pro-solid-svg-icons/faCommentSms';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons/faEnvelope';
import { faPhone } from '@fortawesome/pro-solid-svg-icons/faPhone';
import { faTimer } from '@fortawesome/pro-solid-svg-icons/faTimer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Group, Radio, Stack, Text } from '@mantine/core';
import { FormattedMessage } from 'react-intl';

import { MfaErrorBanner } from './mfa-error-banner';
import { ModalContent } from '../../components/modal-content';
import { ModalFooter } from '../../components/modal-footer';
import { AUTH0_MFA_TYPES, MFA_ENROLLMENT_METHODS } from '../mfa-constants';
import { mfaEnrollmentMessages } from '../mfa-enrollment-messages';

import type { MfaEnrollmentMethod } from '../mfa-constants';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { MessageDescriptor } from 'react-intl';

const METHOD_ICONS: Record<MfaEnrollmentMethod, IconDefinition> = {
  [AUTH0_MFA_TYPES.totp]: faTimer,
  [AUTH0_MFA_TYPES.sms]: faCommentSms,
  [AUTH0_MFA_TYPES.voice]: faPhone,
  [AUTH0_MFA_TYPES.email]: faEnvelope,
};

const METHOD_LABELS: Record<MfaEnrollmentMethod, MessageDescriptor> = {
  [AUTH0_MFA_TYPES.totp]: mfaEnrollmentMessages.methodTotp,
  [AUTH0_MFA_TYPES.sms]: mfaEnrollmentMessages.methodSms,
  [AUTH0_MFA_TYPES.voice]: mfaEnrollmentMessages.methodVoice,
  [AUTH0_MFA_TYPES.email]: mfaEnrollmentMessages.methodEmail,
};

export type EnrollmentMethodPhaseProps = {
  onSelect: (method: MfaEnrollmentMethod) => void;
  isSubmitting: boolean;
  errorMessage?: string;
  /** Backend error code, rendered alongside `errorMessage`. */
  errorCode?: string;
  methods?: readonly MfaEnrollmentMethod[];
};

export const EnrollmentMethodPhase = ({
  onSelect,
  isSubmitting,
  errorMessage,
  errorCode,
  methods = MFA_ENROLLMENT_METHODS,
}: EnrollmentMethodPhaseProps) => {
  const [selectedMethod, setSelectedMethod] = useState<string>(methods[0] ?? '');

  const handleContinue = () => {
    const method = methods.find((candidate) => candidate === selectedMethod);
    if (!method || isSubmitting) {
      return;
    }
    onSelect(method);
  };

  return (
    <>
      <ModalContent>
        <Text fw={600} size='lg'>
          <FormattedMessage {...mfaEnrollmentMessages.selectMethodTitle} />
        </Text>

        <Text size='sm'>
          <FormattedMessage {...mfaEnrollmentMessages.selectMethodSubheader} />
        </Text>

        <Radio.Group onChange={setSelectedMethod} value={selectedMethod}>
          <Stack gap='sm'>
            {methods.map((method) => (
              <Radio
                key={method}
                disabled={isSubmitting}
                label={
                  <Group gap='xs' wrap='nowrap'>
                    <FontAwesomeIcon fixedWidth icon={METHOD_ICONS[method]} />
                    <FormattedMessage {...METHOD_LABELS[method]} />
                  </Group>
                }
                value={method}
              />
            ))}
          </Stack>
        </Radio.Group>

        {errorMessage ? <MfaErrorBanner code={errorCode} message={errorMessage} /> : null}
      </ModalContent>

      <ModalFooter>
        <Button disabled={isSubmitting || selectedMethod === ''} onClick={handleContinue}>
          <FormattedMessage {...mfaEnrollmentMessages.continueButton} />
        </Button>
      </ModalFooter>
    </>
  );
};
